import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../config/branding';
import './VerifyCertificateForm.css';

/**
 * Public Certificate Verification Form
 *
 * Allows users to manually enter a certificate ID or number to verify.
 * Redirects to the verification page after submission.
 */
function VerifyCertificateForm() {
  const navigate = useNavigate();
  const [certificateInput, setCertificateInput] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validate input
    const trimmedInput = certificateInput.trim();

    if (!trimmedInput) {
      setError('Please enter a certificate ID or number');
      return;
    }

    // Redirect to verification page
    navigate(`/verify/${encodeURIComponent(trimmedInput)}`);
  };

  return (
    <div className="verify-form-container">
      <div className="verify-form-content">
        {/* Header */}
        <div className="verify-header">
          <div className="brand-logo">
            <div className="logo-icon">🔍</div>
            <h1>{BRAND.name}</h1>
          </div>
          <p className="tagline">Certificate Verification System</p>
        </div>

        {/* Main Card */}
        <div className="verify-card">
          <h2 className="card-title">Verify Certificate Authenticity</h2>
          <p className="card-description">
            Enter the certificate ID or certificate number to verify its authenticity.
            This information can be found on the certificate document or QR code.
          </p>

          {/* Verification Form */}
          <form onSubmit={handleSubmit} className="verification-form">
            <div className="form-group">
              <label htmlFor="certificateInput" className="form-label">
                Certificate ID or Number
              </label>
              <input
                type="text"
                id="certificateInput"
                className="form-input"
                placeholder="e.g., 123e4567-e89b-12d3-a456-426614174000"
                value={certificateInput}
                onChange={(e) => setCertificateInput(e.target.value)}
                autoFocus
              />
              {error && <div className="form-error">{error}</div>}
            </div>

            <button type="submit" className="verify-button">
              <span className="button-icon">🔍</span>
              Verify Certificate
            </button>
          </form>

          {/* Help Text */}
          <div className="help-section">
            <h3 className="help-title">Where to find the certificate ID?</h3>
            <ul className="help-list">
              <li>
                <span className="help-icon">📄</span>
                Look for the "Certificate ID" field on your certificate document
              </li>
              <li>
                <span className="help-icon">📱</span>
                Scan the QR code on the certificate with your mobile device
              </li>
              <li>
                <span className="help-icon">🔢</span>
                Use the certificate number (e.g., CERT-2025-001)
              </li>
            </ul>
          </div>

          {/* Security Notice */}
          <div className="security-info">
            <div className="security-icon">🔒</div>
            <div className="security-text">
              <p className="security-title">Secure Verification</p>
              <p className="security-description">
                All verifications are performed in real-time against the official {BRAND.short} certification database.
                Your verification request is encrypted and secure.
              </p>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h3 className="feature-title">Instant Verification</h3>
            <p className="feature-description">
              Real-time verification against our certification database
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🌍</div>
            <h3 className="feature-title">Globally Accessible</h3>
            <p className="feature-description">
              Verify certificates from anywhere in the world, 24/7
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🔐</div>
            <h3 className="feature-title">Secure & Private</h3>
            <p className="feature-description">
              No registration required, your verification is confidential
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="verify-footer">
          <p className="footer-text">
            Powered by {BRAND.name} - International Council for Standards & Certification
          </p>
          <div className="footer-links">
            <a href="/privacy" className="footer-link">Privacy Policy</a>
            <span className="footer-separator">·</span>
            <a href="/terms" className="footer-link">Terms of Service</a>
            <span className="footer-separator">·</span>
            <a href="/login" className="footer-link">Login to {BRAND.short}</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyCertificateForm;
